////////////////////////////////////////////
// AUTH PROVIDER — COOKIE SESSION + ROLES (PORT OF web/app/auth.js)
////////////////////////////////////////////
//
// ON LOAD IT SILENTLY ASKS THE SERVER WHO THE SESSION BELONGS TO
// (GET /api/auth/me — HttpOnly COOKIE, UNREADABLE FROM JS). NORMAL VISITORS
// GET 401 -> role "everybody" AND NOTHING ADMIN-ISH EVER RENDERS. signIn()
// IS THE ONE WALLET SIGNATURE AND IS ONLY CALLED FROM AN EXPLICIT USER
// ACTION (ADMIN GATE / LOGIN PAGE) — NEVER ON CONNECT OR PAGE LOAD.
import { createContext, useCallback, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, getJson, postJson } from "@/lib/api/client";
import { useDeeplinkResume } from "@/lib/wallet/deeplink/resume";
import type { AuthMe } from "@/lib/api/types";

const ANON: AuthMe = { role: "everybody" };

interface AuthContextValue {
  me: AuthMe;
  loading: boolean;
  refresh: () => Promise<AuthMe>;
  // signMessage IS INJECTED BY THE CALLER (SOLANA OR EVM WALLET) SO AUTH
  // STAYS WALLET-AGNOSTIC; RETURNS THE SIGNED-IN IDENTITY
  signIn: (wallet: string, signMessage: (message: string) => Promise<string>) => Promise<AuthMe>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {

  const qc = useQueryClient();

  // WALLET-APP DEEPLINK RETURN — FINISH THE CAPTURED FLOW (SESSION / VERIFY /
  // TX SUBMIT) EXACTLY ONCE PER LOAD; MOUNTED HERE FOR THE QUERY CLIENT
  useDeeplinkResume();

  // WHOAMI — SILENT + NON-THROWING; ANY FAILURE DEGRADES TO everybody
  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async (): Promise<AuthMe> => {

      try {
        return await getJson<AuthMe>("/api/auth/me");
      } catch {
        return ANON;
      }
    },
    staleTime: 60_000,
  });

  const refresh = useCallback(async (): Promise<AuthMe> => {

    const r = await qc.fetchQuery({
      queryKey: ["auth", "me"],
      queryFn: async (): Promise<AuthMe> => {

        try {
          return await getJson<AuthMe>("/api/auth/me");
        } catch {
          return ANON;
        }
      },
      staleTime: 0,
    });
    return r;
  }, [qc]);

  // SIGN IN — NONCE -> WALLET SIGNS -> VERIFY (SETS THE SESSION COOKIE).
  // ERRORS CARRY THE SERVER code (e.g. LOGIN_DISABLED) FOR CALLERS TO BRANCH.
  const signIn = useCallback(async (wallet: string, signMessage: (m: string) => Promise<string>): Promise<AuthMe> => {

    const { nonce, message } = await postJson<{ nonce: string; message: string }>("/api/auth/nonce", { wallet });
    const signature = await signMessage(message);
    try {
      const me = await postJson<AuthMe>("/api/auth/verify", { wallet, nonce, signature });
      qc.setQueryData(["auth", "me"], me);
      return me;
    } catch (e) {
      // RE-THROW WITH THE CODE INTACT — ApiError ALREADY CARRIES IT
      if (e instanceof ApiError) throw e;
      throw new Error("sign-in failed");
    }
  }, [qc]);

  const logout = useCallback(async () => {

    try { await postJson("/api/auth/logout", {}); } catch { /* IGNORE */ }
    qc.setQueryData(["auth", "me"], ANON);
  }, [qc]);

  return (
    <AuthContext.Provider value={{ me: meQuery.data ?? ANON, loading: meQuery.isLoading, refresh, signIn, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {

  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

// CONVENIENCE — ROLE CHECKS READ EVERYWHERE
export function useRole() {

  const { me } = useAuth();
  return { role: me.role, isAdmin: me.role === "admin", wallet: me.wallet ?? null, alias: me.alias ?? null };
}
